import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DASHBOARD_RECENT_RESULTS_ID,
  DASHBOARD_UPCOMING_MATCHES_ID
} from '../constants/dashboardAnchors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useCompetition } from '../contexts/CompetitionContext';
import NotificationSettings from '../components/NotificationSettings';
import { PageContainer } from '../components/layout/PageContainer';
import { UserSummaryCard } from '../components/dashboard/UserSummaryCard';
import { UpcomingGameCard } from '../components/dashboard/UpcomingGameCard';
import { RoundFilterTabs } from '../components/dashboard/UpcomingMatchTabs';
import { useRoundFilter } from '../hooks/useRoundFilter';
import { FinishedGameCard } from '../components/dashboard/FinishedGameCard';
import { Button } from '../components/ui/Button';
import {
  fetchCompetitionGames,
  fetchCompetitionLeaderboard,
  fetchMyBetsInCompetition,
  postOrUpdateBet
} from '../services/competitionApi';
import { getCompetitionDisplayName } from '../utils/competitionDisplayName';
import toast from 'react-hot-toast';
import { Bell, CalendarDays, Flag, Save } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { competitionSlug, currentCompetition } = useCompetition();
  const { language, isRTL } = useLanguage();
  const [stats, setStats] = useState({
    totalPoints: 0,
    totalBets: 0,
    correctPredictions: 0,
    rank: 0
  });
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [finishedGames, setFinishedGames] = useState([]);
  const [myBets, setMyBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [savedFlashId, setSavedFlashId] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const { isSupported, isSubscribed } = useNotifications();

  const betByGameId = useMemo(() => {
    const m = {};
    myBets.forEach((b) => {
      const gid = b.gameId?._id || b.gameId;
      if (gid) m[String(gid)] = b;
    });
    return m;
  }, [myBets]);

  const {
    view: upcomingView,
    selectedRound: upcomingRound,
    setSelectedRound: setUpcomingRound,
    rounds: upcomingRounds,
    visibleItems: visibleUpcomingGames,
    handleViewChange: handleUpcomingViewChange
  } = useRoundFilter(upcomingGames, undefined, competitionSlug);

  const {
    view: resultsView,
    selectedRound: resultsRound,
    setSelectedRound: setResultsRound,
    rounds: resultsRounds,
    visibleItems: visibleFinishedGames,
    handleViewChange: handleResultsViewChange
  } = useRoundFilter(finishedGames, undefined, competitionSlug);

  const draftSummary = useMemo(() => {
    const validEntries = [];
    let invalidCount = 0;

    upcomingGames.forEach((game) => {
      if (game.isBettingLocked || game.isLocked) return;

      const gameId = String(game._id);
      const draft = drafts[gameId] || { home: '', away: '' };
      const existing = betByGameId[gameId]?.predictedResult;
      const savedHome = String(existing?.homeScore ?? '');
      const savedAway = String(existing?.awayScore ?? '');
      const home = String(draft.home ?? '').trim();
      const away = String(draft.away ?? '').trim();
      const changed = home !== savedHome || away !== savedAway;

      if (!changed) return;

      if (/^\d+$/.test(home) && /^\d+$/.test(away)) {
        validEntries.push({
          gameId,
          predictedResult: {
            homeScore: parseInt(home, 10),
            awayScore: parseInt(away, 10)
          }
        });
      } else {
        invalidCount += 1;
      }
    });

    return {
      validEntries,
      invalidCount,
      dirtyCount: validEntries.length + invalidCount
    };
  }, [upcomingGames, drafts, betByGameId]);

  const fetchDashboardData = useCallback(async (preservedDrafts = null) => {
    if (!user) return;
    try {
      setLoading(true);
      const [leaderboardRows, bets, finished, upcoming] = await Promise.all([
        fetchCompetitionLeaderboard(competitionSlug),
        fetchMyBetsInCompetition(competitionSlug),
        fetchCompetitionGames(competitionSlug, 'finished'),
        fetchCompetitionGames(competitionSlug, 'upcoming')
      ]);

      setMyBets(bets);

      const myRow = leaderboardRows.find((r) => String(r.userId) === String(user._id));
      const totalPoints =
        myRow?.totalPoints ??
        bets.filter((b) => b.isCalculated).reduce((sum, b) => sum + (b.points || 0), 0);
      const totalBets = bets.length;
      const correctPredictions = bets.filter((b) => b.isCalculated && b.points > 0).length;
      const rank =
        leaderboardRows.findIndex((r) => String(r.userId) === String(user._id)) + 1;

      setStats({
        totalPoints,
        totalBets,
        correctPredictions,
        rank: rank || 0
      });

      setFinishedGames(finished || []);
      setUpcomingGames(upcoming || []);

      const initialDrafts = {};
      (upcoming || []).forEach((g) => {
        const ub = g.userBet;
        if (ub?.predictedResult) {
          const pr = ub.predictedResult;
          initialDrafts[g._id] = {
            home: String(pr.homeScore ?? ''),
            away: String(pr.awayScore ?? '')
          };
        } else {
          const fromBets = bets.find((b) => String(b.gameId?._id || b.gameId) === String(g._id));
          if (fromBets?.predictedResult) {
            const pr = fromBets.predictedResult;
            initialDrafts[g._id] = {
              home: String(pr.homeScore ?? ''),
              away: String(pr.awayScore ?? '')
            };
          } else {
            initialDrafts[g._id] = { home: '', away: '' };
          }
        }
      });
      setDrafts(preservedDrafts ? { ...initialDrafts, ...preservedDrafts } : initialDrafts);
    } catch (e) {
      console.error(e);
      toast.error(language === 'en' ? 'Failed to load dashboard' : 'נכשל בטעינה');
    } finally {
      setLoading(false);
    }
  }, [user, competitionSlug, language]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const anchorId =
      location.hash === `#${DASHBOARD_RECENT_RESULTS_ID}`
        ? DASHBOARD_RECENT_RESULTS_ID
        : location.hash === `#${DASHBOARD_UPCOMING_MATCHES_ID}`
          ? DASHBOARD_UPCOMING_MATCHES_ID
          : null;
    if (!anchorId) return;
    const id = window.setTimeout(() => {
      document.getElementById(anchorId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.hash]);

  const handleSave = async (gameId) => {
    const d = drafts[gameId];
    if (!d || d.home === '' || d.away === '') {
      toast.error(language === 'en' ? 'Enter both scores' : 'הזן שתי תוצאות');
      return;
    }
    const homeScore = parseInt(d.home, 10);
    const awayScore = parseInt(d.away, 10);
    if (Number.isNaN(homeScore) || Number.isNaN(awayScore) || homeScore < 0 || awayScore < 0) {
      toast.error(language === 'en' ? 'Invalid scores' : 'תוצאות לא תקינות');
      return;
    }

    setSavingId(gameId);
    try {
      await postOrUpdateBet(competitionSlug, {
        gameId,
        predictedResult: { homeScore, awayScore }
      });
      setSavedFlashId(gameId);
      setTimeout(() => setSavedFlashId(null), 2200);
      await fetchDashboardData(drafts);
    } catch (err) {
      const msg = err.response?.data?.message || (language === 'en' ? 'Save failed' : 'שמירה נכשלה');
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveAll = async () => {
    const entries = draftSummary.validEntries;
    if (!entries.length) {
      toast.error(
        language === 'en'
          ? 'Complete both scores for at least one changed prediction'
          : 'השלם את שתי התוצאות לפחות בניבוי אחד ששונה'
      );
      return;
    }

    setSavingAll(true);
    try {
      const results = await Promise.allSettled(
        entries.map(({ gameId, predictedResult }) =>
          postOrUpdateBet(competitionSlug, { gameId, predictedResult })
        )
      );
      const savedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results.filter((result) => result.status === 'rejected');

      await fetchDashboardData(drafts);

      if (savedCount > 0) {
        toast.success(
          language === 'en'
            ? `Saved ${savedCount} prediction${savedCount === 1 ? '' : 's'}`
            : `נשמרו ${savedCount} ניבויים`
        );
      }
      if (failed.length > 0) {
        const firstError =
          failed[0].reason?.response?.data?.message ||
          (language === 'en'
            ? `${failed.length} prediction(s) failed to save`
            : `${failed.length} ניבויים לא נשמרו`);
        toast.error(firstError);
      }
    } finally {
      setSavingAll(false);
    }
  };

  const setDraft = (gameId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [gameId]: {
        ...prev[gameId],
        [field]: value
      }
    }));
  };

  return (
    <PageContainer dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="animate-fade-in space-y-10">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-950 via-red-950 to-brand-950 px-6 py-8 text-white shadow-2xl ring-1 ring-white/10 sm:px-10 sm:py-10">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-brand-400/30 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute bottom-0 left-0 h-48 w-48 rounded-full bg-rose-500/25 blur-2xl"
            aria-hidden
          />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-200/95">
                {language === 'en' ? 'Active competition' : 'תחרות פעילה'}
              </p>
              <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                {getCompetitionDisplayName(currentCompetition, language) ||
                  competitionSlug}
              </h1>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-200/95">
                {language === 'en'
                  ? `Hi ${user?.username || 'there'} — enter home & away scores before kickoff. Locked matches can’t be changed.`
                  : `היי ${user?.username || ''} — הזן תוצאות בית וחוץ לפני תחילת המשחק. משחק נעול לא ניתן לשינוי.`}
              </p>
            </div>
            {isSupported && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowNotificationSettings(true)}
                className="shrink-0 border-white/20 bg-white/10 text-white hover:bg-white/20"
              >
                <Bell className="h-4 w-4" />
                {isSubscribed
                  ? language === 'en'
                    ? 'Notifications'
                    : 'התראות'
                  : language === 'en'
                    ? 'Enable alerts'
                    : 'הפעל התראות'}
              </Button>
            )}
          </div>
        </section>

        <div className="relative z-10 -mt-6 md:-mt-10">
          <UserSummaryCard
            loading={loading}
            totalPoints={stats.totalPoints}
            rank={stats.rank}
            totalBets={stats.totalBets}
            correctPredictions={stats.correctPredictions}
            title={language === 'en' ? 'Your season' : 'העונה שלך'}
            subtitle={
              language === 'en'
                ? 'Points and rank for this competition'
                : 'נקודות ודירוג לתחרות זו'
            }
            pointsSuffix={language === 'en' ? 'pts' : 'נק׳'}
            statLabels={{
              rank: language === 'en' ? 'Rank' : 'דירוג',
              bets: language === 'en' ? 'Bets' : 'הימורים',
              hits: language === 'en' ? 'Hits' : 'פגיעות'
            }}
          />
        </div>

        <section
          id={DASHBOARD_UPCOMING_MATCHES_ID}
          className="scroll-mt-28 space-y-5 sm:scroll-mt-32"
          aria-label={language === 'en' ? 'Upcoming matches' : 'משחקים קרובים'}
        >
          {/* Row + justify-start: in RTL, main-start is inline-start (physical right). */}
          <div
            dir={language === 'he' ? 'rtl' : 'ltr'}
            className="space-y-4 border-b border-slate-200/80 pb-4"
          >
            <div className="flex w-full flex-row justify-start">
              <div className="flex max-w-full items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                  <CalendarDays className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="text-start">
                  <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    {language === 'en' ? 'Upcoming matches' : 'משחקים קרובים'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    {language === 'en'
                      ? 'Fill several predictions, then save them all at once.'
                      : 'מלא כמה ניבויים ושמור את כולם בבת אחת.'}
                  </p>
                </div>
              </div>
            </div>
            <RoundFilterTabs
              language={language}
              view={upcomingView}
              onViewChange={handleUpcomingViewChange}
              selectedRound={upcomingRound}
              onRoundChange={setUpcomingRound}
              rounds={upcomingRounds}
              selectId="upcoming-round-filter"
              ariaLabel={language === 'en' ? 'Upcoming match filters' : 'סינון משחקים קרובים'}
            />
          </div>
          {loading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-200/80" />
              ))}
            </div>
          ) : upcomingGames.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-slate-500">
              {language === 'en' ? 'No upcoming matches.' : 'אין משחקים קרובים.'}
            </p>
          ) : visibleUpcomingGames.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-slate-500">
              {upcomingView === 'round' && !upcomingRound
                ? language === 'en'
                  ? 'Select a round to see matches.'
                  : 'בחר סיבוב כדי לראות משחקים.'
                : language === 'en'
                  ? 'No upcoming matches in this round.'
                  : 'אין משחקים קרובים בסיבוב זה.'}
            </p>
          ) : (
            <div className="space-y-4">
              {visibleUpcomingGames.map((game) => (
                <UpcomingGameCard
                  key={game._id}
                  game={game}
                  homeInput={drafts[game._id]?.home ?? ''}
                  awayInput={drafts[game._id]?.away ?? ''}
                  onHomeChange={(v) => setDraft(game._id, 'home', v)}
                  onAwayChange={(v) => setDraft(game._id, 'away', v)}
                  onSave={() => handleSave(game._id)}
                  saving={savingId === game._id}
                  saveDisabled={savingAll}
                  savedFlash={savedFlashId === game._id}
                  language={language}
                />
              ))}
            </div>
          )}
        </section>

        {!loading && draftSummary.dirtyCount > 0 && (
          <div
            dir={isRTL ? 'rtl' : 'ltr'}
            className="fixed inset-x-0 bottom-4 z-50 mx-auto w-[calc(100%-2rem)] max-w-2xl"
          >
            <div className="flex flex-col gap-3 rounded-2xl border border-brand-200 bg-white/95 p-3 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:p-4">
              <div className="min-w-0 text-start">
                <p className="font-semibold text-slate-900">
                  {language === 'en'
                    ? `${draftSummary.dirtyCount} unsaved prediction${
                        draftSummary.dirtyCount === 1 ? '' : 's'
                      }`
                    : `${draftSummary.dirtyCount} ניבויים שלא נשמרו`}
                </p>
                {draftSummary.invalidCount > 0 && (
                  <p className="text-xs text-amber-700">
                    {language === 'en'
                      ? `${draftSummary.invalidCount} incomplete — enter both scores`
                      : `${draftSummary.invalidCount} לא הושלמו — יש להזין שתי תוצאות`}
                  </p>
                )}
              </div>
              <Button
                variant="primary"
                className="shrink-0"
                disabled={draftSummary.validEntries.length === 0 || savingId !== null}
                loading={savingAll}
                onClick={handleSaveAll}
              >
                <Save className="h-4 w-4" />
                {language === 'en'
                  ? `Save all (${draftSummary.validEntries.length})`
                  : `שמור הכל (${draftSummary.validEntries.length})`}
              </Button>
            </div>
          </div>
        )}

        <section
          id={DASHBOARD_RECENT_RESULTS_ID}
          className="scroll-mt-28 space-y-5 sm:scroll-mt-32"
          aria-label={language === 'en' ? 'Recent results' : 'תוצאות אחרונות'}
        >
          <div
            dir={language === 'he' ? 'rtl' : 'ltr'}
            className="space-y-4 border-b border-slate-200/80 pb-4"
          >
            <div className="flex w-full flex-row justify-start">
              <div className="flex max-w-full items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                  <Flag className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="text-start">
                  <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    {language === 'en' ? 'Recent results' : 'תוצאות אחרונות'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    {language === 'en'
                      ? 'Final scores vs your pick — green is exact, amber is right winner, red is miss.'
                      : 'תוצאה סופית מול הניבוי שלך — ירוק מדויק, כתום כיוון, אדום החטאה.'}
                  </p>
                </div>
              </div>
            </div>
            <RoundFilterTabs
              language={language}
              view={resultsView}
              onViewChange={handleResultsViewChange}
              selectedRound={resultsRound}
              onRoundChange={setResultsRound}
              rounds={resultsRounds}
              allLabel={language === 'en' ? 'All results' : 'כל התוצאות'}
              selectId="recent-round-filter"
              ariaLabel={language === 'en' ? 'Recent result filters' : 'סינון תוצאות אחרונות'}
            />
          </div>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200/80" />
              ))}
            </div>
          ) : finishedGames.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-slate-500">
              {language === 'en' ? 'No finished matches yet.' : 'אין עדיין משחקים שהסתיימו.'}
            </p>
          ) : visibleFinishedGames.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-slate-500">
              {resultsView === 'round' && !resultsRound
                ? language === 'en'
                  ? 'Select a round to see results.'
                  : 'בחר סיבוב כדי לראות תוצאות.'
                : language === 'en'
                  ? 'No results in this round.'
                  : 'אין תוצאות בסיבוב זה.'}
            </p>
          ) : (
            <div className="space-y-3">
              {visibleFinishedGames.map((game) => (
                <FinishedGameCard
                  key={game._id}
                  game={game}
                  userBet={betByGameId[String(game._id)]}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {showNotificationSettings && (
        <NotificationSettings onClose={() => setShowNotificationSettings(false)} />
      )}
    </PageContainer>
  );
};

export default Dashboard;
